import React, { useCallback, useEffect, useState } from 'react'
import { useBlocker } from 'react-router'
import { ExclamationCircleFilled } from '@ant-design/icons'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

interface UseUnsavedChangesBlockerOptions {
  hasUnsavedChanges: () => boolean
  onDiscard?: () => void
  title?: string
  message?: string
}

/**
 * Generic hook to block navigation when there are unsaved changes
 * Can be used with any grid or form that tracks unsaved changes
 *
 * Provides a simple confirmation dialog with two options:
 * - Cancel: Stay on page (user can save manually)
 * - Discard and navigate: Discard changes and proceed with navigation
 *
 * @param options - Configuration options
 * @param options.hasUnsavedChanges - Function that returns boolean indicating if there are unsaved changes
 * @param options.onDiscard - Optional callback to discard changes before navigating
 * @param options.title - Optional custom title for the confirmation dialog
 * @param options.message - Optional custom message for the confirmation dialog
 *
 * @example
 * ```tsx
 * const { hasUnsavedChanges, discardChanges } = useGridDataActions({ gridApi })
 * useUnsavedChangesBlocker({
 *   hasUnsavedChanges,
 *   onDiscard: discardChanges
 * })
 * ```
 */
const useUnsavedChangesBlocker = ({
  hasUnsavedChanges,
  onDiscard,
  title,
  message
}: UseUnsavedChangesBlockerOptions) => {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const [modalInstance, setModalInstance] = useState<ReturnType<typeof modal.confirm> | null>(null)

  // Block navigating elsewhere when there are unsaved changes
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => hasUnsavedChanges() && currentLocation.pathname !== nextLocation.pathname
  )

  // Show modal when navigation is blocked
  const showModal = useCallback(() => {
    if (blocker.state === 'blocked' && !modalInstance) {
      let instance: ReturnType<typeof modal.confirm>

      // Create handlers with direct instance reference
      const handleCancel = () => {
        instance?.destroy()
        setModalInstance(null)
        if (blocker.state === 'blocked') {
          blocker.reset()
        }
      }

      const handleDiscardAndNavigate = () => {
        onDiscard?.()
        instance?.destroy()
        setModalInstance(null)
        if (blocker.state === 'blocked') {
          blocker.proceed()
        }
      }

      instance = modal.confirm({
        icon: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
        title: title || t('modal:discardChangesTitle', 'Muokkaustilan sulkeminen.'),
        width: 500,
        content: message || t('modal:discardChangesContent', 'Haluatko sulkea muokkaustilan tallentamatta?'),
        closable: true,
        okText: t('global:ok', 'Kyllä'),
        cancelText: t('global:no', 'Ei'),
        okButtonProps: {
          style: {
            width: 100
          }
        },
        cancelButtonProps: {
          style: {
            width: 100
          }
        },
        onCancel: handleCancel,
        onOk: handleDiscardAndNavigate
      })
      setModalInstance(instance)
    }
  }, [blocker, modalInstance, title, message, t, onDiscard, modal])

  useEffect(() => {
    showModal()
  }, [showModal])
}

export default useUnsavedChangesBlocker
